
import React, { useState, useEffect } from 'react';
import { MarketAnalysisForm } from '@/components/admin/market-summary/MarketAnalysisForm';
import { MarketSummaryTable } from '@/components/admin/market-summary/MarketSummaryTable';
import { MarketDataAnalysisHeader } from '@/components/admin/market-analysis/MarketDataAnalysisHeader';
import { MarketDataActionButtons } from '@/components/admin/market-analysis/MarketDataActionButtons';
import { MarketAnalysisDetailDialog } from '@/components/admin/market-analysis/MarketAnalysisDetailDialog';
import { ScheduledMarketAnalysisJobsManagement } from '@/components/admin/market-analysis/ScheduledMarketAnalysisJobsManagement';
import { DataResetDangerZone } from '@/components/admin/market-analysis/DataResetDangerZone';
import { MarketSummaryService, type MarketSummary } from '@/services/MarketSummaryService';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const MarketDataAnalysisPage = () => {
  const [summaries, setSummaries] = useState<MarketSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState<MarketSummary | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);

  useEffect(() => {
    loadSummaries();
  }, []);

  const loadSummaries = async () => {
    setIsLoading(true);
    try {
      const data = await MarketSummaryService.getMarketSummaries(20);
      setSummaries(data);
    } catch (error) {
      console.error('Error loading summaries:', error);
      toast.error('Failed to load market summaries');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnalysisComplete = async (summaryId: string) => {
    await loadSummaries();
    const newSummary = summaries.find(s => s.id === summaryId);
    if (newSummary) {
      setSelectedSummary(newSummary);
      setIsDetailDialogOpen(true);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const result = await MarketSummaryService.deleteMarketSummary(id);
      if (result.success) {
        toast.success('Market summary deleted');
        setSummaries(prev => prev.filter(s => s.id !== id));
      } else {
        toast.error(result.error || 'Failed to delete summary');
      }
    } catch (error) {
      console.error('Error deleting summary:', error);
      toast.error('Failed to delete summary');
    }
  };

  const handleView = (summary: MarketSummary) => {
    setSelectedSummary(summary);
    setIsDetailDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-4 sm:space-y-6">
        <MarketDataAnalysisHeader />
        
        <MarketDataActionButtons 
          onRefresh={loadSummaries}
          isLoading={isLoading}
        />

        <Tabs defaultValue="manual" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="manual">Manual Analysis</TabsTrigger>
            <TabsTrigger value="scheduled">Scheduled Jobs</TabsTrigger>
            <TabsTrigger value="reset">Data Reset</TabsTrigger>
          </TabsList>
          
          <TabsContent value="manual" className="space-y-4 sm:space-y-6">
            <MarketAnalysisForm
              onAnalysisComplete={handleAnalysisComplete}
              isAnalyzing={isAnalyzing}
              setIsAnalyzing={setIsAnalyzing}
            />

            <MarketSummaryTable
              summaries={summaries}
              onDelete={handleDelete}
              onView={handleView}
              isLoading={isLoading}
            />
          </TabsContent>
          
          <TabsContent value="scheduled" className="space-y-4 sm:space-y-6">
            <ScheduledMarketAnalysisJobsManagement />
          </TabsContent>
          
          <TabsContent value="reset" className="space-y-4 sm:space-y-6">
            <DataResetDangerZone />
          </TabsContent>
        </Tabs>

        <MarketAnalysisDetailDialog
          isOpen={isDetailDialogOpen}
          onOpenChange={setIsDetailDialogOpen}
          selectedSummary={selectedSummary}
        />
      </div>
    </div>
  );
};

export default MarketDataAnalysisPage;
